<script lang="ts">
    import {createEventDispatcher, getContext, onDestroy, onMount, setContext} from 'svelte';
    import {INJECTOR_KEY} from '../../../svelte/utils';
    import type {Injector} from '../../../svelte/types';
    import {ValueEditorTypeResolutionService} from './value-editor-type-resolution.service';
    import type {ValueEditorTypeOrAlias} from '../types';
    import {ValueEditorSize} from '../types';
    import type {FormGroupBuilder} from '../internal/forms/form-group';
    import type {ValueEditorLocalizations} from '../localizations/types';
    import type {ValueEditorOptions, ValueEditorValidations} from './types';
    import {ASYNC_VALIDATION_DEFAULT_OPTIONS, asyncValidator} from '../internal/async-validations';
    import type {ForceSettingsOptionsMerger} from '../kp-value-editor-force-settings/force-settings-options-merger';
    import {ValueEditorConfigurationManagerProvider} from '../configuration/value-editor-configuration-manager.provider';
    import {ValueEditorLocalizationManagerProvider} from '../localizations/value-editor-localization-manager.provider';
    import type {ModelInputChangeEvent, ValueEditorsEventMap} from '../events';
    import type {Subscription} from 'rxjs';
    import type {FormController, FormControlOptions} from '../internal/forms/types';
    import {uuid as generateUuid} from '../../../shared/utils/custom-utils';
    import type {Readable} from 'svelte/store';
    import {ErrorMessagesLocalizationService} from '../errors/error-messages-localization.service';
    import {KpValueEditorConfigurationService} from './kp-value-editor-configuration.service';
    import {merge} from 'lodash';
    import {
        EDITORS_LOCALIZE,
        FORCE_SETTINGS,
        FORM_BUILDER,
        UNIVERSAL_FORM_MODEL
    } from '../context-keys';
    import type {EditorsLocalizationFunction} from '../localizations/types';
    import {defaultUpdateTriggers} from '../internal/forms/form-control';
    import {isMetaEditor} from './utils';

    type MODEL_VALUE = $$Generic;

    export let type: ValueEditorTypeOrAlias;

    export let model: MODEL_VALUE;
    export let formControlOptions: FormControlOptions | null = null;

    export let editorId: string = null;
    export let editorName: string = editorId ?? `${type}_${generateUuid()}`;
    export let placeholder = '';

    export let isDisabled = false;
    export let isVisible = true;
    export let isFocused = false;
    export let forceShowErrors = false;
    export let size: ValueEditorSize = ValueEditorSize.MD;

    export let options: ValueEditorOptions = {};
    export let validations: ValueEditorValidations = {};
    export let localizations: ValueEditorLocalizations = null;

    const dispatch = createEventDispatcher<ValueEditorsEventMap<MODEL_VALUE>>();
    const injector: Injector = getContext(INJECTOR_KEY);

    const configuration: KpValueEditorConfigurationService = injector.getByToken(KpValueEditorConfigurationService.serviceName);
    const errorMessagesLocalizationService: ErrorMessagesLocalizationService = injector.getByToken(ErrorMessagesLocalizationService.serviceName);
    const editorResolutionService: ValueEditorTypeResolutionService = injector.getByToken(ValueEditorTypeResolutionService.serviceName);
    const configurationManagerProvider: ValueEditorConfigurationManagerProvider = injector.getByToken(ValueEditorConfigurationManagerProvider.serviceName);
    const localizationManagerProvider: ValueEditorLocalizationManagerProvider = injector.getByToken(ValueEditorLocalizationManagerProvider.serviceName);

    const realType = editorResolutionService.resolveRealType(type);
    const editor = editorResolutionService.getEditorComponentByTypeOrAlias(type);
    const universalFormModel$: Readable<Record<string, unknown>> = getContext(UNIVERSAL_FORM_MODEL);
    const formBuilder: FormGroupBuilder<Record<string, unknown>> = getContext(FORM_BUILDER);
    const forceSettings: ForceSettingsOptionsMerger = getContext(FORCE_SETTINGS);
    let editorController; // component binding
    let modelChangesSubscription: Subscription;


    const localize: EditorsLocalizationFunction<ValueEditorLocalizations> = (code: keyof ValueEditorLocalizations) => {
        if (localizations?.[code]) {
            return localizations[code];
        }

        if (localizationManagerProvider.getManagerForType(realType)) {
            return localizationManagerProvider.getManagerForType(realType).getLocalization<any>(code);
        } else {
            throw new Error('LocalizationManager is not set');
        }
    }

    setContext(EDITORS_LOCALIZE, localize);

    function processOptions(newOptions: ValueEditorOptions) {

        const optionsFromConfiguration = configurationManagerProvider.getManagerForType(realType).forAlias(type).getConfiguration();

        let updatedOptions = merge({}, optionsFromConfiguration, newOptions);

        if (forceSettings) {
            updatedOptions = forceSettings.mergeOptionsForType(type, updatedOptions)
        }

        return updatedOptions;
    }

    $: options = processOptions(options);

    if (validations.async && !formControlOptions) {
        // add debounce if async validation is present
        formControlOptions = {updateTriggers: [defaultUpdateTriggers.BLUR, {eventName: 'input', debounceValue: 500}]};
    }

    function dispatchModelChangeEvent(event: ModelInputChangeEvent<MODEL_VALUE>) {
        // Event fired when model value is changed via some input element. Payload is new model value.
        dispatch('model-change', event.detail);
        // literal event name instead of constant is used, because otherwise STORYBOOK does not recognize the event
    }
    onMount(() => {
        modelChangesSubscription = getFormController().getModelInputChangeEvent$().subscribe((event) => {
            dispatchModelChangeEvent(event);
        });

        // add async validations
        if (validations.async) {
            const validationFunction = configuration.getValidationsFunction();
            let nullIfEmptyModifier: <T>(value: T) => T | null;
            if (options?.emptyAsNull) {
                nullIfEmptyModifier = (value) => !value ? null : value;
            }
            const asyncOptions = typeof validations.async === 'boolean' ? ASYNC_VALIDATION_DEFAULT_OPTIONS : {...ASYNC_VALIDATION_DEFAULT_OPTIONS, ...validations.async};
            getFormController().addAsyncValidator(asyncValidator(validationFunction, errorMessagesLocalizationService, editorName, universalFormModel$, asyncOptions, nullIfEmptyModifier))
            getFormController().validate();
        }

        if (formBuilder) {
            formBuilder.registerFormControl(getFormController()); // register to the nearest parent meta editor
        }
    })

    onDestroy(() => {
        modelChangesSubscription?.unsubscribe()
    })

    export function getFormController(): FormController<MODEL_VALUE> {
        return editorController.getFormController();
    }

</script>

<template>
    <kp-value-editor class:hidden={!isVisible} data-type={realType} data-type-or-alias={type}>
        <svelte:component this={editor}
                          bind:this={editorController}

                          bind:model
                          {formControlOptions}

                          {editorId}
                          {editorName}
                          {placeholder}


                          {...isMetaEditor(realType) /* conditional 'isVisible' property */ ? {isVisible} : {}}
                          {isDisabled}
                          {isFocused}
                          {forceShowErrors}
                          {size}

                          options={options ?? {}}
                          validations={validations ?? {}}

                          on:form-submit-request
                          on:form-structure-change/>
    </kp-value-editor>
</template>

<style global lang="less">
    @import "bootstrap-less/bootstrap/variables";
    @import "bootstrap-less/bootstrap/mixins/forms";
    @import "bootstrap-less/bootstrap/mixins/vendor-prefixes";

    .has-error {
        kp-value-editor {
            .input-group .btn-default, .form-control-button {
                border-color: @state-danger-text;
                transition: border-color 0.15s ease-in-out;
            }

            /* reset error styles if editor is nested in meta-editor which has error */
            .has-not-error {
                .input-group .btn-default, .form-control-button {
                    border-color: @input-border;
                    transition: border-color 0.15s ease-in-out;
                }

                .form-control-validation(@input-color, @input-border, @input-bg);
            }
        }
    }

    kp-value-editor {

        .input-xs {
            height: 22px;
            padding: 1px 5px;
            font-size: 12px;
            line-height: 1.5;
            border-radius: 3px;
        }

        .input-group.input-group-xs {
            .input-group-addon,
            .input-group-btn .btn {
                height: 22px;
                padding: 3px 5px 0 5px;
                font-size: 12px;
                line-height: 1.3;
            }

            > .input-group-addon {
                border-radius: 3px 0 0 3px;
            }

            .input-group-addon:last-child {
                border-radius: 0 3px 3px 0;
            }
        }

        .input-group-select {
            padding: 0;
            border: none;
        }
    }
</style>
